import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    cssClass = "cssClass",
    email = "email",
    phone = "phone",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.cssClass]: string;
        [fields.email]: string;
        [fields.phone]: number;
    };
}

type Props = OwnProps;

export interface SurTabularDataStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    title?: TypographyProps;
    ctaContainer?: GridContainerProps;
    ctaButton?: GridItemProps;
    spacer?: GridItemProps;
    cta?: LinkProps;
}
export const tabularDataStyles: SurTabularDataStyles = {
    container: {
        css: css`
            margin-top: 70px;
            background-color: ${getColor("common.backgroundContrast")};
            padding: 60px 20px;
        `,
    },
    wrapper: {
        css: css`
            width: 660px;
            max-width: 95%;
            margin: 0 auto;
            text-align: center;
        `,
    },
    title: {
        variant: "h6",
        css: css`
            text-align: center;
            margin-bottom: 30px;
        `,
    },
    ctaContainer: {
        css: css`
            margin-left: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-left: 0;
                        `,
                        css`
                            margin-left: 0;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    ctaButton: {
        width: [12, 5, 5, 5, 5],
        css: css`
            padding-left: 0;
            padding-right: 0;
        `,
    },
    spacer: {
        width: [12, 1, 1, 1, 1],
        css: css`
            align-items: center;
            text-align: center;
            padding: 0;
            strong {
                display: block;
                width: 100%;
            }
        `,
    },
    cta: {
        css: css`
            display: block;
            border: 1px solid;
            width: 100%;
            padding: 10px 0;
            font-size: ${TYPOGRAPHY.size.base};
            span {
                color: ${getColor("text.main")} !important;
            }
        `,
    },
};

const styles = tabularDataStyles;
const emailsRegexp = new RegExp(
    "^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$",
);
const phoneRegexp = /^[+]?(\d{1,2})?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

const SurFaqCtaButtons: React.FC<Props> = ({ fields, id }) => {
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.wrapper}>
                {fields.title ? <Typography {...styles.title}>{fields.title}</Typography> : null}
                <GridContainer {...styles.ctaContainer}>
                    <GridItem {...styles.ctaButton}>
                        <Link {...styles.cta} href={`mailto:${fields.email}`}>
                            Email Us at <strong>{fields.email}</strong>
                        </Link>
                    </GridItem>
                    <GridItem {...styles.spacer}>
                        <strong>OR</strong>
                    </GridItem>
                    <GridItem {...styles.ctaButton}>
                        <Link {...styles.cta} href={`tel:${fields.phone}`}>
                            Call Us at <strong>{fields.phone}</strong>
                        </Link>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const SurFaqCtaButtonsWidget: WidgetModule = {
    component: SurFaqCtaButtons,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.email,
                displayName: "Email",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "example@email.com",
                isRequired: true,
                regularExpression: emailsRegexp,
                sortOrder: 1,
            },
            {
                name: fields.phone,
                displayName: "Contact Number",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
                regularExpression: new RegExp(phoneRegexp),
                sortOrder: 2,
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default SurFaqCtaButtonsWidget;
